import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import InspectionStatusPill from './InspectionStatusPill';

export function evaluateToleranceStatus(toleranceValue, standards) {
  if (toleranceValue === null || toleranceValue === undefined || toleranceValue === '' || !standards) return undefined;
  if (toleranceValue === 'N/A') return 'N/A';
  try {
    const { operator, standard, tolerance, tolerance_type } = standards;
    const val = parseFloat(toleranceValue);
    if (operator === '>') return val > parseFloat(standard);
    if (operator === '<') return val < parseFloat(standard);
    if (operator === '≥') return val >= parseFloat(standard);
    if (operator === '≤') return val <= parseFloat(standard);
    let stdNumeric;
    if (typeof standard === 'string' && standard.includes(':')) {
      const [num, den] = standard.split(':');
      stdNumeric = parseFloat(num) / parseFloat(den);
    } else {
      stdNumeric = parseFloat((operator || '') + standard);
    }
    let checkedValue;
    if (typeof toleranceValue === 'string' && toleranceValue.includes(':')) {
      const [num, den] = toleranceValue.split(':');
      checkedValue = parseFloat(num) / parseFloat(den);
    } else {
      checkedValue = val;
    }
    const tol = parseFloat(tolerance);
    return !tolerance || tolerance_type === 'capture_input'
      || (checkedValue <= stdNumeric + tol && checkedValue >= stdNumeric - tol);
  } catch {
    return undefined;
  }
}

export const InspectionDropdown = injectIntl((props) => {
  const {
    intl,
    handleColumnStatusChange,
    selectedValue,
    isActive,
    disabled,
    enableNA,
    toleranceInput,
    toleranceValue,
    standards,
    nomenclature,
    defaultSatisfactory,
    onToggle
  } = props;

  const isCaptureInput = toleranceInput;
  const hasStandard = standards?.standard !== undefined
    && standards?.standard !== null
    && standards?.standard !== '';
  const isAutoCaptureInput = isCaptureInput && hasStandard;
  const isManualCaptureInput = isCaptureInput && !hasStandard;

  const emptyLabel = isCaptureInput
    ? intl.formatMessage({ id: 'inspection.status.select_status_enter_value', defaultMessage: 'Select Status / Enter Value' })
    : intl.formatMessage({ id: 'inspection.status.select_status', defaultMessage: 'Select Status' });

  const textStyle = isCaptureInput ? { fontSize: '13px' } : undefined;
  let statusOptions;
  let allowSelection;

  if (isAutoCaptureInput) {
    statusOptions = enableNA ? ['N/A'] : [];
    allowSelection = statusOptions.length > 0;
  } else if (isManualCaptureInput) {
    statusOptions = [true, false, ...(enableNA ? ['N/A'] : [])];
    allowSelection = true;
  } else {
    allowSelection = !toleranceInput;
  }

  let status;

  if (selectedValue === 'N/A') {
    status = 'N/A';
  } else if (toleranceInput && !isManualCaptureInput) {
    status = evaluateToleranceStatus(toleranceValue, standards);
  } else {
    status = selectedValue;
  }

  let inspectionNames = nomenclature;
  if (!inspectionNames || Object.keys(inspectionNames).length === 0) {
    try {
      inspectionNames = JSON.parse(localStorage.getItem('insp_nom')) || {};
    } catch (error) {
      inspectionNames = {};
    }
  }

  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <InspectionStatusPill
        status={status}
        onChange={handleColumnStatusChange}
        enableNA={enableNA}
        disabled={!isActive || disabled}
        isTolerance={toleranceInput && !allowSelection && selectedValue !== 'N/A'}
        nomenclature={inspectionNames}
        defaultSatisfactory={defaultSatisfactory}
        onToggle={onToggle}
        statusOptions={statusOptions}
        allowSelection={allowSelection}
        forceSelectStatusLabel={isCaptureInput}
        emptyLabel={emptyLabel}
        textStyle={textStyle}
      />
    </div>
  );
});

InspectionDropdown.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  handleColumnStatusChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  enableNA: PropTypes.bool,
  toleranceInput: PropTypes.bool,
  toleranceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  standards: PropTypes.shape({}),
  nomenclature: PropTypes.shape({}),
  defaultSatisfactory: PropTypes.bool,
  onToggle: PropTypes.func
};

InspectionDropdown.defaultProps = {
  selectedValue: undefined,
  isActive: true,
  disabled: false,
  enableNA: false,
  toleranceInput: false,
  toleranceValue: '',
  standards: {},
  nomenclature: {},
  defaultSatisfactory: false,
  onToggle: () => {}
};

export default InspectionDropdown;
